import React, { useState, useEffect, useMemo } from "react";
import {
  FaPlus,
  FaSave,
  FaUser,
  FaMapMarkerAlt,
  FaShoppingCart,
  FaCalendarAlt,
  FaDollarSign,
  FaInfoCircle,
  FaEdit,
  FaTrash,
  FaExclamationTriangle,
  FaCalculator,
  FaTruck,
  FaCreditCard,
  FaBoxes
} from "react-icons/fa";
import OrderItemBuilder from "./OrderItemBuilder";
import InterestCalculator from "../common/InterestCalculator";
import { getData } from "../../services/api.service";
import { provinciasArgentinas, getCiudadesByProvincia } from "../../utils/argentinaLocations";

function OrderFormModal({ 
  isOpen, 
  onClose, 
  onSave, 
  order = null,
  clients = [],
  skus = [],
  materials = [],
  colors = [],
  accessories = [],
  orderStatuses = [],
  paymentMethods = [],
  paymentForms = [],
  deliveryTypes = [],
  ecommerceTypes = [],
  isEditing = false 
}) {
  const [customerMode, setCustomerMode] = useState('new'); // 'new' or 'existing'
  const [availableCities, setAvailableCities] = useState([]);
  const [formData, setFormData] = useState({
    // Cliente existente
    customer_id: '',
    // Cliente nuevo
    customer_name: '',
    customer_dni: '',
    customer_phone: '',
    customer_email: '',
    customer_location: '',
    customer_province: '',
    customer_city: '',
    is_wholesale: false,
    // Datos del pedido
    order_date: new Date().toISOString().split('T')[0],
    status: '', // Se cargará en blanco pero se enviará el primer estado disponible
    delivery_type: '',
    payment_method: '',
    payment_form: '', // Forma de pago (Efectivo, TC, TD, etc.)
    payment_installments: 0, // Cuotas de pago
    ecommerce_type: '', // Tipo de ecommerce (1 = NO ES ECOMMERCE por defecto)
    estimated_delivery_days: 0, // Días estimados de entrega
    credited_payment: 0,
    credit_balance: 0,
    total_amount: 0,
    notes: '',
    items: []
  });

  const [errors, setErrors] = useState({});
  const [showItemBuilder, setShowItemBuilder] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [interestData, setInterestData] = useState(null); // Datos del cálculo de interés

  useEffect(() => {
    if (order) {      
      // Para edición, siempre usar el cliente existente del pedido
      // En modo edición no se debería cambiar de cliente
      setCustomerMode('existing');
      
      // Cargar ciudades basado en la provincia del pedido
      const customerProvince = order.customer_province || '';
      
      // Si hay provincia, cargar ciudades disponibles
      if (customerProvince) {
        setAvailableCities(getCiudadesByProvincia(customerProvince));
      }
      
      setFormData({
        // Cliente - usar los datos del pedido existente
        customer_id: order.client_id || '',
        customer_name: order.client_name || '',
        customer_dni: order.client_dni || '',
        customer_phone: order.client_cellphone || '', // Nota: campo diferente en la respuesta
        customer_email: '', // No disponible en este endpoint
        customer_location: order.client_address || '', // Dirección del cliente
        customer_province: order.customer_province || '',
        customer_city: order.customer_city || '',
        is_wholesale: order.is_wholesale || false,
        
        // Datos del pedido
        order_date: order.inserted_date ? order.inserted_date.split('T')[0] : new Date().toISOString().split('T')[0],
        status: order.status || '',
        delivery_type: order.delivery_type || '',
        payment_method: order.payment_method || '',
        payment_form: order.payment_form || '',
        payment_installments: order.payment_installments || 0,
        ecommerce_type: order.ecommerce_type || '',
        estimated_delivery_days: order.estimated_delivery_days !== undefined ? order.estimated_delivery_days : null,
        credited_payment: order.credited_payment || 0,
        credit_balance: order.credit_balance || 0,
        total_amount: order.total || 0, // Campo correcto para el total
        notes: order.observations || '', // Campo correcto para observaciones
        
        // Datos de interés (al editar)
        interest_rate: order.interest_rate || null,
        interest_amount: order.interest_amount || null,
        total_with_interest: order.total_with_interest || null,
        installment_amount: order.installment_amount || null,
        
        // Items del pedido - mapear correctamente la estructura
        items: order.items ? order.items.map(item => {
          return {
            item_id: item.item_id, // Incluir item_id para actualizaciones
            sku_id: item.sku_id,
            quantity: item.quantity,
            sale_price: item.sale_price,
            details: {
              material_id: item.details?.material_id || '',
              measurements: item.details?.measurements || '',
              accessory_id: item.details?.accessory_id || null,
              additional_accessories: (item.details?.additional_accessories || []).map(acc => 
                typeof acc === 'object' ? acc.accessory_id : acc
              ),
              colors: (item.details?.colors || []).map(color => 
                typeof color === 'object' ? color.color_id : color
              )
            },
            // Incluir productos de stock local si existen
            stock_products: (item.stock_products || []).map(sp => ({
              product_id: sp.product_id,
              product_name: sp.product_name,
              quantity: sp.quantity,
              unit_price: sp.unit_price
            }))
          };
        }) : []
      });
    } else {
      // Reset form for new order
      setCustomerMode('new');
      setAvailableCities([]);
      setFormData({
        customer_id: '',
        customer_name: '',
        customer_dni: '',
        customer_phone: '',
        customer_email: '',
        customer_location: '',
        customer_province: '',
        customer_city: '',
        is_wholesale: false,
        order_date: new Date().toISOString().split('T')[0],
        status: '', // Se cargará en blanco inicialmente
        delivery_type: '',
        payment_method: '',
        payment_installments: 0,
        ecommerce_type: '', // Se cargará en blanco, por defecto será 1 en el backend
        estimated_delivery_days: null, // Se calculará automáticamente según ecommerce_type
        credited_payment: 0,
        credit_balance: 0,
        total_amount: 0,
        notes: '',
        items: []
      });
    }
  }, [order, isOpen, orderStatuses]);

  useEffect(() => {
    // Recalcular total automáticamente cuando cambien los ítems
    const total = formData.items.reduce((sum, item) => {
      // Total del producto principal
      const itemTotal = parseFloat(item.sale_price) * parseInt(item.quantity);
      
      // Total de productos de stock local del ítem
      const stockTotal = (item.stock_products || []).reduce((stockSum, sp) => {
        return stockSum + (parseFloat(sp.unit_price) * parseInt(sp.quantity));
      }, 0);
      
      return sum + itemTotal + stockTotal;
    }, 0);
    
    setFormData(prev => ({
      ...prev,
      total_amount: total
    }));
  }, [formData.items]);

  // useEffect para calcular automáticamente el tiempo de entrega
  useEffect(() => {
    const calculateDeliveryTime = () => {
      if (!formData.ecommerce_type) return;
      
      // Buscar el tipo seleccionado directamente
      const selectedType = ecommerceTypes.find(
        type => type.id.toString() === formData.ecommerce_type.toString()
      );
      
      // Usar el tiempo de entrega del tipo de ecommerce
      if (selectedType && selectedType.estimated_delivery_days) {
        setFormData(prev => ({
          ...prev,
          estimated_delivery_days: selectedType.estimated_delivery_days
        }));
      } else {
        // Fallback: valor por defecto si no está configurado
        setFormData(prev => ({
          ...prev,
          estimated_delivery_days: 15
        }));
      }
    };
    
    calculateDeliveryTime();
  }, [formData.ecommerce_type, ecommerceTypes]);

  const handleInputChange = async (e) => {
    const { name, value, type } = e.target;
    
    // Si cambia la provincia, actualizar las ciudades disponibles
    if (name === 'customer_province') {
      setAvailableCities(getCiudadesByProvincia(value));
      // Limpiar la ciudad seleccionada cuando cambie la provincia
      setFormData(prev => ({
        ...prev,
        customer_province: value,
        customer_city: ''
      }));
    } else if (name === 'customer_dni' && value.length >= 7 && customerMode === 'new') {
      // Buscar cliente por DNI cuando tiene al menos 7 dígitos
      try {
        const response = await getData(`/api/get_client_by_dni?dni=${value}`, false);
        
        if (response && response.data && response.data.success) {
          const client = response.data.data;
          
          // Autocompletar datos del cliente
          const cities = getCiudadesByProvincia(client.customer_province || '');
          setAvailableCities(cities);
          
          setFormData(prev => ({
            ...prev,
            customer_dni: value,
            customer_name: client.client_name || '',
            customer_phone: client.client_cellphone || '',
            customer_location: client.client_address || '',
            customer_province: client.customer_province || '',
            customer_city: client.customer_city || '',
            is_wholesale: client.is_wholesale || false
          }));
        } else {
          // Cliente no encontrado, solo actualizar el DNI
          setFormData(prev => ({
            ...prev,
            [name]: value
          }));
        }
      } catch (error) {
        // Si hay error (404 o similar), continuar normalmente
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
      }));
    }
    
    // Limpiar error del campo cuando se modifica
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleItemSave = (itemData) => {
  
    if (editingItemIndex >= 0) {
      // Editando ítem existente
      const updatedItems = [...formData.items];
      updatedItems[editingItemIndex] = itemData;
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
    } else {
      // Agregando nuevo ítem
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, itemData]
      }));
    }
    
    setShowItemBuilder(false);
    setEditingItemIndex(-1);
  };

  const handleItemCancel = () => {
    setShowItemBuilder(false);
    setEditingItemIndex(-1);
  };

  const handleInterestCalculated = (calculatedData) => {
    // Guardar los datos del interés calculado
    setInterestData(calculatedData);
  };

  const handleEditItem = (index) => {
    setEditingItemIndex(index);
    setShowItemBuilder(true);
  };

  const handleDeleteItem = (index) => {
    if (window.confirm('¿Está seguro de que desea eliminar este ítem?')) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar datos del cliente según el modo
    if (customerMode === 'existing') {
      if (!formData.customer_id) {
        newErrors.customer_id = 'Debe seleccionar un cliente';
      }
    } else {
      // Modo nuevo cliente
      if (!formData.customer_name.trim()) {
        newErrors.customer_name = 'El nombre del cliente es requerido';
      }
      if (!formData.customer_dni.trim()) {
        newErrors.customer_dni = 'El DNI del cliente es requerido';
      }
      if (!formData.customer_phone.trim()) {
        newErrors.customer_phone = 'El teléfono del cliente es requerido';
      }
    }

    if (!formData.customer_location.trim()) {
      newErrors.customer_location = 'La ubicación es requerida';
    }

    if (!formData.customer_province.trim()) {
      newErrors.customer_province = 'La provincia es requerida';
    }

    if (!formData.customer_city.trim()) {
      newErrors.customer_city = 'La ciudad es requerida';
    }

    if (!formData.order_date) {
      newErrors.order_date = 'La fecha del pedido es requerida';
    }

    if (!formData.delivery_type) {
      newErrors.delivery_type = 'Debe seleccionar un tipo de entrega';
    }

    if (!formData.payment_method) {
      newErrors.payment_method = 'Debe seleccionar un método de pago';
    }

    if (!formData.payment_form) {
      newErrors.payment_form = 'Debe seleccionar una forma de pago';
    }

    if (!formData.ecommerce_type) {
      newErrors.ecommerce_type = 'Debe seleccionar un canal de venta';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Debe agregar al menos un ítem al pedido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const orderDataToSave = {
        ...formData,
        interest_rate: interestData ? interestData.interest_rate : null,
        interest_amount: interestData ? interestData.interest_amount : null,
        total_with_interest: interestData ? interestData.total_with_interest : null,
        installment_amount: interestData ? interestData.installment_amount : null
      };

      const saved = await onSave(orderDataToSave);
      // Solo cerrar el modal si la operación fue exitosa (onSave debe retornar true)
      if (saved) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving order:', error);
      const msg = error?.response?.data?.msg || error?.message || 'Error al guardar el pedido. Intente nuevamente.';
      setErrors({ submit: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getSkuName = (skuId) => {
    if (!skuId || skuId === null) return 'Sin SKU (Stock Local)';
    const sku = skus.find(s => s.id.toString() === skuId.toString());
    return sku ? (sku.sku_description || sku.sku_name || `SKU #${sku.id}`) : 'SKU no encontrado';
  };

  const getMaterialName = (materialId) => {
    if (!materialId || materialId === null) return 'Sin material';
    const material = materials.find(m => m.id.toString() === materialId.toString());
    return material ? material.material_name : 'Material no encontrado';
  };

  // Construir datos iniciales de interés al editar un pedido existente
  const initialInterestData = useMemo(() => {
    if (isEditing && order && order.interest_rate && order.interest_amount) {
      return {
        order_total: order.total || 0,
        interest_rate: order.interest_rate,
        interest_amount: order.interest_amount,
        total_with_interest: order.total_with_interest || ((order.total || 0) + order.interest_amount),
        installment_amount: order.installment_amount || (((order.total || 0) + order.interest_amount) / (order.payment_installments || 1)),
        installments: order.payment_installments || 1,
        description: `${order.interest_rate}% interés`
      };
    }
    return null;
  }, [isEditing, order]);

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <FaShoppingCart className="me-2 text-primary" />
              {isEditing ? 'Editar Pedido' : 'Nuevo Pedido'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isLoading}
            ></button>
          </div>

          {!showItemBuilder ? (
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {errors.submit && (
                  <div className="alert alert-danger">
                    <FaExclamationTriangle className="me-2" />
                    {errors.submit}
                  </div>
                )}

                <div className="row">
                  {/* Customer Mode Selector - Solo mostrar en modo creación */}
                  {!isEditing && (
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FaUser className="me-1" />
                        Tipo de Cliente
                      </label>
                      <div className="btn-group w-100" role="group">
                        <input
                          type="radio"
                          className="btn-check"
                          name="customerMode"
                          id="customerModeNew"
                          checked={customerMode === 'new'}
                          onChange={() => setCustomerMode('new')}
                        />
                        <label className="btn btn-outline-primary" htmlFor="customerModeNew">
                          <FaUser className="me-1" />
                          Cliente Nuevo
                        </label>

                        <input
                          type="radio"
                          className="btn-check"
                          name="customerMode"
                          id="customerModeExisting"
                          checked={customerMode === 'existing'}
                          onChange={() => setCustomerMode('existing')}
                        />
                        <label className="btn btn-outline-primary" htmlFor="customerModeExisting">
                          <FaUser className="me-1" />
                          Cliente Existente (Mayorista)
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {/* Información del cliente en modo edición */}
                  {isEditing && (
                    <div className="col-md-12 mb-3">
                      <div className="alert alert-info">
                        <FaInfoCircle className="me-2" />
                        <strong>Editando pedido #{order?.order_id}</strong>
                        <br />
                        Cliente: {formData.customer_name || 'Cliente directo'} 
                        {formData.customer_dni && ` (DNI: ${formData.customer_dni})`}
                        {formData.is_wholesale ? ' (Mayorista)' : ' (Minorista)'}
                      </div>
                    </div>
                  )}
                </div>

                {/* En edición: No mostrar selector de mayorista si el pedido es minorista */}
                {customerMode === 'existing' && !isEditing && (
                  // Cliente existente (mayorista)
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FaUser className="me-1" />
                        Cliente Mayorista *
                      </label>
                      <select
                        className={`form-select ${errors.customer_id ? 'is-invalid' : ''}`}
                        name="customer_id"
                        value={formData.customer_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Seleccionar Cliente</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.client_name} - {client.client_id}
                          </option>
                        ))}
                      </select>
                      {errors.customer_id && <div className="invalid-feedback">{errors.customer_id}</div>}
                    </div>
                  </div>
                )}
                
                {/* Cliente nuevo o en edición con datos minoristas */}
                {(customerMode === 'new' || (isEditing && customerMode === 'new')) && (
                  // Cliente nuevo
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FaUser className="me-1" />
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.customer_name ? 'is-invalid' : ''}`}
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        placeholder="Nombre y apellido del cliente"
                        disabled={isEditing} // Deshabilitar en modo edición
                        required
                      />
                      {errors.customer_name && <div className="invalid-feedback">{errors.customer_name}</div>}
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        <FaUser className="me-1" />
                        DNI *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.customer_dni ? 'is-invalid' : ''}`}
                        name="customer_dni"
                        value={formData.customer_dni}
                        onChange={handleInputChange}
                        placeholder="12345678"
                        disabled={isEditing} // Deshabilitar en modo edición
                        required
                      />
                      {errors.customer_dni && <div className="invalid-feedback">{errors.customer_dni}</div>}
                    </div>

                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        <FaUser className="me-1" />
                        Teléfono *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.customer_phone ? 'is-invalid' : ''}`}
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        placeholder="3511234567"
                        required
                      />
                      {errors.customer_phone && <div className="invalid-feedback">{errors.customer_phone}</div>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FaUser className="me-1" />
                        Email
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        placeholder="cliente@email.com"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FaUser className="me-1" />
                        ¿Es cliente mayorista?
                      </label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_wholesale"
                          checked={formData.is_wholesale}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            is_wholesale: e.target.checked
                          }))}
                        />
                        <label className="form-check-label">
                          Cliente mayorista
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  {/* Location */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      <FaMapMarkerAlt className="me-1" />
                      Dirección de Entrega *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.customer_location ? 'is-invalid' : ''}`}
                      name="customer_location"
                      value={formData.customer_location}
                      onChange={handleInputChange}
                      placeholder="Calle, número, piso, departamento, etc."
                      required
                    />
                    {errors.customer_location && <div className="invalid-feedback">{errors.customer_location}</div>}
                  </div>
                </div>

                <div className="row">
                  {/* Province */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaMapMarkerAlt className="me-1" />
                      Provincia *
                    </label>
                    <select
                      className={`form-select ${errors.customer_province ? 'is-invalid' : ''}`}
                      name="customer_province"
                      value={formData.customer_province}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccionar Provincia</option>
                      {provinciasArgentinas.map(provincia => (
                        <option key={provincia.id} value={provincia.nombre}>
                          {provincia.nombre}
                        </option>
                      ))}
                    </select>
                    {errors.customer_province && <div className="invalid-feedback">{errors.customer_province}</div>}
                  </div>

                  {/* City */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaMapMarkerAlt className="me-1" />
                      Ciudad *
                    </label>
                    <select
                      className={`form-select ${errors.customer_city ? 'is-invalid' : ''}`}
                      name="customer_city"
                      value={formData.customer_city}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.customer_province}
                    >
                      <option value="">
                        {formData.customer_province ? 'Seleccionar Ciudad' : 'Primero seleccione una provincia'}
                      </option>
                      {availableCities.map((ciudad, index) => (
                        <option key={index} value={ciudad}>
                          {ciudad}
                        </option>
                      ))}
                    </select>
                    {errors.customer_city && <div className="invalid-feedback">{errors.customer_city}</div>}
                  </div>
                </div>

                <div className="row">
                  {/* Order Date */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      <FaCalendarAlt className="me-1" />
                      Fecha del Pedido *
                    </label>
                    <input
                      type="date"
                      className={`form-control ${errors.order_date ? 'is-invalid' : ''}`}
                      name="order_date"
                      value={formData.order_date}
                      onChange={handleInputChange}
                      required
                    />
                    {errors.order_date && <div className="invalid-feedback">{errors.order_date}</div>}
                  </div>

                  {/* Delivery Type */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      <FaTruck className="me-1" />
                      Tipo de Entrega *
                    </label>
                    <select
                      className={`form-select ${errors.delivery_type ? 'is-invalid' : ''}`}
                      name="delivery_type"
                      value={formData.delivery_type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccionar Tipo</option>
                      {deliveryTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.delivery_description}
                        </option>
                      ))}
                    </select>
                    {errors.delivery_type && <div className="invalid-feedback">{errors.delivery_type}</div>}
                  </div>

                  {/* Payment Method */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      <FaCreditCard className="me-1" />
                      Método de Pago *
                    </label>
                    <select
                      className={`form-select ${errors.payment_method ? 'is-invalid' : ''}`}
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccionar Método</option>
                      {paymentMethods.map(method => (
                        <option key={method.id} value={method.id}>
                          {method.payment_description}
                        </option>
                      ))}
                    </select>
                    {errors.payment_method && <div className="invalid-feedback">{errors.payment_method}</div>}
                  </div>
                </div>

                <div className="row">
                  {/* Ecommerce Type */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      <FaShoppingCart className="me-1" />
                      Canal de Venta *
                    </label>
                    <select
                      className={`form-select ${errors.ecommerce_type ? 'is-invalid' : ''}`}
                      name="ecommerce_type"
                      value={formData.ecommerce_type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccionar Canal</option>
                      {ecommerceTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.ecommerce_name}
                        </option>
                      ))}
                    </select>
                    {errors.ecommerce_type && <div className="invalid-feedback">{errors.ecommerce_type}</div>}
                    <small className="form-text text-muted">
                      Indica si la venta proviene de un ecommerce
                    </small>
                    {(formData.estimated_delivery_days !== null && formData.estimated_delivery_days !== undefined) && (
                      <div className="alert alert-info mt-2 py-2 px-3 small mb-0">
                        <FaTruck className="me-2" />
                        <strong>Entrega estimada:</strong> {formData.estimated_delivery_days} {formData.estimated_delivery_days === 1 ? 'día hábil' : 'días hábiles'}
                      </div>
                    )}
                  </div>

                  {/* Forma de Pago */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      <FaCreditCard className="me-1" />
                      Forma de Pago *
                    </label>
                    <select
                      className={`form-select ${errors.payment_form ? 'is-invalid' : ''}`}
                      name="payment_form"
                      value={formData.payment_form}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccionar Forma</option>
                      {paymentForms.map(form => (
                        <option key={form.id} value={form.id}>
                          {form.form_description}
                        </option>
                      ))}
                    </select>
                    {errors.payment_form && <div className="invalid-feedback">{errors.payment_form}</div>}
                    <small className="form-text text-muted">
                      ¿Cómo pagará? (Efectivo, TC, TD, etc.)
                    </small>
                  </div>

                  {/* Payment Installments */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      <FaCreditCard className="me-1" />
                      Cuotas de Pago
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="payment_installments"
                      value={formData.payment_installments}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0 = Pago único"
                    />
                    <small className="form-text text-muted">
                      0 = Pago en una sola cuota
                    </small>
                  </div>

                  {/* Calculadora de Interés */}
                  {formData.payment_form && formData.payment_installments > 0 && formData.total_amount > 0 && (
                    <div className="col-12">
                      <InterestCalculator
                        orderTotal={formData.total_amount}
                        paymentFormId={formData.payment_form}
                        installments={formData.payment_installments}
                        onInterestCalculated={handleInterestCalculated}
                        initialInterestData={initialInterestData}
                      />
                    </div>
                  )}

                  {/* Total Amount (Read-only) */}
                  <div className={`${interestData && interestData.interest_rate > 0 ? 'col-md-6' : 'col-md-8'} mb-3`}>
                    <label className="form-label">
                      <FaDollarSign className="me-1" />
                      Total del Pedido (sin interés)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formatCurrency(formData.total_amount)}
                      readOnly
                    />
                    <small className="form-text text-muted">
                      Suma de productos vendidos
                    </small>
                  </div>

                  {/* Total con Interés (si aplica) */}
                  {interestData && interestData.interest_rate > 0 && (
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FaDollarSign className="me-1 text-success" />
                        <strong>Total a Cobrar (con interés)</strong>
                      </label>
                      <input
                        type="text"
                        className="form-control fw-bold text-success border-success"
                        value={formatCurrency(interestData.total_with_interest)}
                        readOnly
                      />
                      <small className="form-text text-success">
                        <i className="bi bi-info-circle me-1"></i>
                        Esto es lo que pagará el cliente
                      </small>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="mb-3">
                  <label className="form-label">
                    <FaInfoCircle className="me-1" />
                    Notas
                  </label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Notas adicionales sobre el pedido..."
                  ></textarea>
                </div>

                <hr />

                {/* Order Items Section */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <FaShoppingCart className="me-2" />
                    Ítems del Pedido ({formData.items.length})
                  </h6>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowItemBuilder(true)}
                  >
                    <FaPlus className="me-1" />
                    Agregar Ítem
                  </button>
                </div>

                {errors.items && (
                  <div className="alert alert-danger">
                    <FaExclamationTriangle className="me-2" />
                    {errors.items}
                  </div>
                )}

                {formData.items.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Material</th>
                          <th>Medidas</th>
                          <th>Cantidad</th>
                          <th>Precio Unit.</th>
                          <th>Subtotal</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => {
                          // Determinar si es un ítem solo de stock (sin SKU)
                          const isStockOnlyItem = !item.sku_id && item.stock_products && item.stock_products.length > 0;
                          
                          return (
                          <React.Fragment key={index}>
                            {/* Si tiene SKU, mostrar fila normal del producto */}
                            {!isStockOnlyItem && (
                              <tr>
                                <td>
                                  <small>{getSkuName(item.sku_id)}</small>
                                </td>
                                <td>
                                  <small>{getMaterialName(item.details.material_id)}</small>
                                </td>
                                <td>
                                  <small>{item.details.measurements}</small>
                                </td>
                                <td>{item.quantity}</td>
                                <td>{formatCurrency(item.sale_price)}</td>
                                <td>
                                  <strong>{formatCurrency(item.sale_price * item.quantity)}</strong>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm me-1"
                                    onClick={() => handleEditItem(index)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeleteItem(index)}
                                  >
                                    <FaTrash />
                                  </button>
                                </td>
                              </tr>
                            )}
                            
                            {/* Si es ítem solo de stock, mostrar encabezado especial */}
                            {isStockOnlyItem && (
                              <tr className="table-success">
                                <td colSpan="5">
                                  <strong>
                                    <FaBoxes className="me-2 text-success" />
                                    Productos de Stock Local (sin fabricación)
                                  </strong>
                                </td>
                                <td>
                                  <strong>
                                    {formatCurrency(
                                      item.stock_products.reduce((sum, sp) => 
                                        sum + (sp.unit_price * sp.quantity), 0
                                      )
                                    )}
                                  </strong>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm me-1"
                                    onClick={() => handleEditItem(index)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeleteItem(index)}
                                  >
                                    <FaTrash />
                                  </button>
                                </td>
                              </tr>
                            )}
                            
                            {/* Productos de stock local del ítem */}
                            {item.stock_products && item.stock_products.length > 0 && (
                              item.stock_products.map((sp, spIndex) => (
                                <tr key={`${index}-stock-${spIndex}`} className="table-light">
                                  <td colSpan="2" className="ps-4">
                                    <small className="text-muted">
                                      <FaBoxes className="me-1 text-success" />
                                      {sp.product_name} (Stock Local)
                                    </small>
                                  </td>
                                  <td>-</td>
                                  <td><small>{sp.quantity}</small></td>
                                  <td><small>{formatCurrency(sp.unit_price)}</small></td>
                                  <td>
                                    <small className="text-success">
                                      {formatCurrency(sp.unit_price * sp.quantity)}
                                    </small>
                                  </td>
                                  <td></td>
                                </tr>
                              ))
                            )}
                          </React.Fragment>
                        );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan="5" className="text-end">Total:</th>
                          <th colSpan="2">
                            <FaCalculator className="me-2" />
                            {formatCurrency(formData.total_amount)}
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="alert alert-light text-center">
                    <FaInfoCircle className="me-2" />
                    No hay ítems agregados al pedido
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || formData.items.length === 0}
                >
                  {isLoading ? (
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  ) : (
                    <FaSave className="me-2" />
                  )}
                  {isEditing ? 'Actualizar Pedido' : 'Crear Pedido'}
                </button>
              </div>
            </form>
          ) : (
            // Item Builder Modal Content
            <div className="modal-body p-0">
              <OrderItemBuilder
                item={editingItemIndex >= 0 ? formData.items[editingItemIndex] : null}
                onSave={handleItemSave}
                onCancel={handleItemCancel}
                skus={skus}
                materials={materials}
                colors={colors}
                accessories={accessories}
                isEditing={editingItemIndex >= 0}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderFormModal;

